/**
 * useParlayTracking - Manages parlay tracking actions, tracking state (spinner keys),
 * partial-recovery flow, and add-to-builder recovery.
 */

import { useQuotaCoin } from '@contexts/QuotaCoinContext';
import { useToast } from '@contexts/ToastContext';
import type { ParlayScore } from '@hooks/useStrategyStats';
import type {
  FailedLegInfo,
  NewParlay,
  NewParlayLeg,
  ParlayStats,
  TrackedParlay,
  TrackParlayResult,
} from '@hooks/useTrackedParlays';
import type { GeneratedParlay } from '@parlay-picker/shared/api/picksApi';
import { BETTING_TIPS } from '@parlay-picker/shared/bettingTips';
import type { BuilderLeg } from '@parlay-picker/shared/builderLeg';
import type { BetContext } from '@parlay-picker/shared/contracts';
import { isTeamBet } from '@parlay-picker/shared/correlationUtils';
import { deriveParlayLeague } from '@parlay-picker/shared/league';
import type { LegSignatureInput } from '@parlay-picker/shared/parlaySignature';
import { createParlayKey, isParlayInTrackedList } from '@parlay-picker/shared/parlaySignature';
import { deriveStoredRecommendation } from '@parlay-picker/shared/recommendation';
import { DEFAULT_RAW_THRESHOLDS } from '@parlay-picker/shared/scoreDisplay';
import { useParlayBuilderStore } from '@stores/parlayBuilderStore';
import type React from 'react';
import { useRef, useState } from 'react';

interface TrackingErrorState {
  message: string;
  failedLegs: FailedLegInfo[];
  healthyLegs: NewParlayLeg[];
  parlayMetadata: {
    tier?: string;
    confidence?: string;
    reasoning?: string;
    sportsbook?: string;
  };
}

interface InlineParlayScore {
  strategyScore: ParlayScore | null;
  predictionScore: ParlayScore | null;
}

interface UseParlayTrackingOptions {
  trackedParlays: TrackedParlay[];
  trackedParlayStats: ParlayStats;
  trackParlayProp?: (data: NewParlay) => Promise<TrackParlayResult>;
  recs: GeneratedParlay[];
  inlineScores: InlineParlayScore[];
  selectedGames: string[];
  generationId: string | null;
  selectedSportsbook: string;
  setError: (err: { message: string; type: string } | null) => void;
  setShowUpgradeModal: (v: boolean) => void;
}

export interface UseParlayTrackingResult {
  trackingKeys: Set<string>;
  trackingError: TrackingErrorState | null;
  isParlayTracked: (rec: GeneratedParlay) => boolean;
  trackParlay: (rec: GeneratedParlay, tierName: string) => Promise<void>;
  handleTrackHealthyLegs: (
    healthyLegs: NewParlayLeg[],
    metadata: {
      tier?: string;
      confidence?: string;
      reasoning?: string;
      sportsbook?: string;
    },
  ) => Promise<void>;
  handleAddToBuilder: (builderInputLegs: NewParlayLeg[]) => void;
  setTrackingError: React.Dispatch<React.SetStateAction<TrackingErrorState | null>>;
}

export function useParlayTracking({
  trackedParlays,
  trackedParlayStats,
  trackParlayProp,
  recs,
  inlineScores,
  selectedGames,
  generationId,
  selectedSportsbook,
  setError,
  setShowUpgradeModal: _setShowUpgradeModal,
}: UseParlayTrackingOptions): UseParlayTrackingResult {
  const { showCoin } = useQuotaCoin();
  const { showToast } = useToast();
  const { addAllLegs: addLegsToBuilder, setExpanded: setBuilderExpanded } = useParlayBuilderStore();

  const [trackingKeys, setTrackingKeys] = useState<Set<string>>(new Set());
  const [trackingError, setTrackingError] = useState<TrackingErrorState | null>(null);
  const trackingInProgressRef = useRef(new Set<string>());

  const getParlayTrackingKey = (rec: GeneratedParlay): string => {
    const legs: LegSignatureInput[] = (rec.legs ?? []).map((leg) => ({
      player: isTeamBet(leg.propType) ? leg.team || leg.player || '' : leg.player || '',
      statType: leg.statType || '',
      line: leg.line,
      overUnder: leg.overUnder,
      gameDate: leg.gameDate,
      leagueKey: leg.leagueKey,
    }));
    return createParlayKey(legs);
  };

  const isParlayTracked = (rec: GeneratedParlay): boolean => {
    if (!rec.legs || rec.legs.length === 0) return false;
    const recLegs: LegSignatureInput[] = rec.legs.map((leg) => {
      const isTeamBetLeg = isTeamBet(leg.propType);
      return {
        player: isTeamBetLeg ? leg.team || leg.player || '' : leg.player || '',
        statType: leg.statType || '',
        line: leg.line,
        overUnder: leg.overUnder,
        gameDate: leg.gameDate,
        leagueKey: leg.leagueKey,
      };
    });
    const trackedLegSets = trackedParlays.map((tp) =>
      tp.legs.map(
        (leg): LegSignatureInput => ({
          player: leg.player || '',
          statType: leg.statType || '',
          line: leg.line,
          overUnder: leg.overUnder,
          gameDate: leg.gameDate,
          leagueKey: leg.leagueKey,
        }),
      ),
    );
    return isParlayInTrackedList(recLegs, trackedLegSets);
  };

  const trackParlay = async (rec: GeneratedParlay, tierName: string): Promise<void> => {
    if (isParlayTracked(rec)) return;
    const { sportsbook } = rec;
    if (!sportsbook) {
      setError({
        message: 'This parlay names no sportsbook, so it cannot be tracked.',
        type: 'track_error',
      });
      return;
    }

    const trackKey =
      rec.legs
        ?.map((l) => `${l.player}:${l.statType}:${l.line}:${l.overUnder}`)
        .sort()
        .join('|') ?? '';
    if (trackingInProgressRef.current.has(trackKey)) return;
    trackingInProgressRef.current.add(trackKey);

    const parlayKey = getParlayTrackingKey(rec);
    setTrackingKeys((prev) => new Set(prev).add(parlayKey));

    const recIndex = recs.indexOf(rec);
    const recScores = recIndex >= 0 ? inlineScores[recIndex] : undefined;
    const stratScore = recScores?.strategyScore;
    const predScore = recScores?.predictionScore;

    const parlayData: NewParlay = {
      tier: tierName,
      sportsbook,
      league: rec.leagueKey,
      legs: (rec.legs || []).map((leg) => {
        return {
          player: isTeamBet(leg.propType) ? leg.team || leg.player || '' : leg.player || '',
          playerId: leg.playerId ? Number(leg.playerId) : null,
          team: leg.team,
          opponentTeam: leg.opponentTeam,
          prop: leg.prop || '',
          statType: leg.statType,
          line: leg.line,
          overUnder: leg.overUnder,
          odds: leg.odds,
          gameId: leg.gameId,
          gameDate: leg.gameDate,
          gameStartsAt: leg.gameStartsAt,
          betContext: leg.betContext as BetContext | null | undefined,
          aiEdge: leg.analysis?.edge || null,
          isB2b: typeof leg.isB2b === 'boolean' ? leg.isB2b : null,
          blowoutRisk: typeof leg.blowoutRisk === 'string' ? leg.blowoutRisk : null,
          isHomeGame: leg.isHomeGame ?? null,
          marketKey: leg.marketKey,
          // Picks generated before 2026-10-01 carry no per-leg league; the parlay's is theirs.
          leagueKey: leg.leagueKey ?? rec.leagueKey,
          provenance: 'generation',
          predictionId: leg.predictionId,
        };
      }),
      totalOdds: rec.totalOdds || rec.odds,
      confidence: rec.confidence,
      reasoning: rec.reasoning,
      selectedGameIds: selectedGames,
      generationId: generationId || undefined,
      trackedStrategyScore: stratScore?.score,
      trackedPredictionScore: predScore?.score,
      trackedStrategyRec:
        stratScore?.score != null
          ? deriveStoredRecommendation(stratScore.score, stratScore.thresholds)
          : undefined,
      trackedPredictionRec:
        predScore?.score != null
          ? deriveStoredRecommendation(predScore.score, predScore.thresholds)
          : undefined,
      trackedScoreBreakdown:
        stratScore || predScore
          ? {
              strategy: {
                total: stratScore?.score ?? 0,
                recommendation:
                  stratScore?.score != null
                    ? deriveStoredRecommendation(stratScore.score, stratScore.thresholds)
                    : '',
                factors: Object.fromEntries(
                  (stratScore?.factors ?? []).map((f) => [f.factor, f.score]),
                ),
              },
              prediction: {
                total: predScore?.score ?? 0,
                recommendation:
                  predScore?.score != null
                    ? deriveStoredRecommendation(predScore.score, predScore.thresholds)
                    : '',
                factors: Object.fromEntries(
                  (predScore?.factors ?? []).map((f) => [f.factor, f.score]),
                ),
              },
              thresholds: {
                bet:
                  stratScore?.thresholds?.bet ??
                  predScore?.thresholds?.bet ??
                  DEFAULT_RAW_THRESHOLDS.bet,
                skip:
                  stratScore?.thresholds?.skip ??
                  predScore?.thresholds?.skip ??
                  DEFAULT_RAW_THRESHOLDS.skip,
              },
            }
          : undefined,
    };

    try {
      if (trackParlayProp) {
        const isFirstTrack = trackedParlayStats.total === 0;
        const result = await trackParlayProp(parlayData);
        if (result.success && result.quotaData) {
          showCoin(result.quotaData);
          if (isFirstTrack) {
            showToast(BETTING_TIPS.firstTrack.body, 'info', 6000);
            try {
              const raw = localStorage.getItem('seenBettingTips');
              const seen: string[] = raw ? JSON.parse(raw) : [];
              if (!seen.includes(BETTING_TIPS.firstTrack.id)) {
                seen.push(BETTING_TIPS.firstTrack.id);
                localStorage.setItem('seenBettingTips', JSON.stringify(seen));
              }
            } catch {
              /* ignore */
            }
          }
          if (
            result.quotaData.type === 'track' &&
            result.quotaData.remaining <= 5 &&
            result.quotaData.remaining > 0
          ) {
            showToast(
              `${result.quotaData.remaining} tracking slot${result.quotaData.remaining === 1 ? '' : 's'} remaining today`,
              'warning',
            );
          }
        }
        if (!result.success) {
          setTrackingKeys((prev) => {
            const next = new Set(prev);
            next.delete(parlayKey);
            return next;
          });
          if (result.recoveryData?.healthyLegs?.length) {
            setTrackingError({
              message: result.error || 'Some legs are missing game data',
              failedLegs: result.recoveryData.failedLegs || [],
              healthyLegs: result.recoveryData.healthyLegs,
              parlayMetadata: {
                tier: tierName,
                confidence: rec.confidence,
                reasoning: rec.reasoning,
                sportsbook: rec.sportsbook,
              },
            });
          } else if (result.quotaInfo) {
            const resetTime = result.quotaInfo.resetsAt
              ? new Date(result.quotaInfo.resetsAt).toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : 'midnight ET';
            setError({
              message: `You've used ${result.quotaInfo.used}/${result.quotaInfo.limit} tracked parlays today. Resets at ${resetTime}.`,
              type: 'track_quota_exceeded',
            });
          } else if (result.error) {
            setError({ message: result.error, type: 'track_error' });
          }
        }
        if (result.success) {
          setTrackingKeys((prev) => {
            const next = new Set(prev);
            next.delete(parlayKey);
            return next;
          });
        }
      } else {
        setTrackingKeys((prev) => {
          const next = new Set(prev);
          next.delete(parlayKey);
          return next;
        });
      }
    } catch {
      setTrackingKeys((prev) => {
        const next = new Set(prev);
        next.delete(parlayKey);
        return next;
      });
    } finally {
      trackingInProgressRef.current.delete(trackKey);
    }
  };

  const handleTrackHealthyLegs = async (
    healthyLegs: NewParlayLeg[],
    metadata: {
      tier?: string;
      confidence?: string;
      reasoning?: string;
      sportsbook?: string;
    },
  ): Promise<void> => {
    if (!trackParlayProp) return;
    const { sportsbook } = metadata;
    if (!sportsbook) {
      setError({
        message: 'This parlay names no sportsbook, so it cannot be tracked.',
        type: 'track_error',
      });
      return;
    }
    const smallerParlay: NewParlay = {
      legs: healthyLegs,
      tier: metadata.tier,
      confidence: metadata.confidence,
      reasoning: metadata.reasoning,
      sportsbook,
      selectedGameIds: selectedGames,
      generationId: generationId || undefined,
      league: deriveParlayLeague(healthyLegs.map((leg) => leg.leagueKey)),
    };
    const result = await trackParlayProp(smallerParlay);
    if (result.success && result.quotaData) {
      showCoin(result.quotaData);
      if (
        result.quotaData.type === 'track' &&
        result.quotaData.remaining <= 5 &&
        result.quotaData.remaining > 0
      ) {
        showToast(
          `${result.quotaData.remaining} tracking slot${result.quotaData.remaining === 1 ? '' : 's'} remaining today`,
          'warning',
        );
      }
    }
    if (result.success) {
      setTrackingError(null);
    } else if (result.quotaInfo) {
      const resetTime = result.quotaInfo.resetsAt
        ? new Date(result.quotaInfo.resetsAt).toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })
        : 'midnight ET';
      setError({
        message: `You've used ${result.quotaInfo.used}/${result.quotaInfo.limit} tracked parlays today. Resets at ${resetTime}.`,
        type: 'track_quota_exceeded',
      });
      setTrackingError(null);
    } else if (result.error) {
      setError({ message: result.error, type: 'track_error' });
      setTrackingError(null);
    }
  };

  const handleAddToBuilder = (builderInputLegs: NewParlayLeg[]): void => {
    const builderLegs: BuilderLeg[] = builderInputLegs.map((leg) => ({
      id: `recovery-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      player: leg.player || null,
      playerId: leg.playerId || null,
      team: leg.team || '',
      opponentTeam: leg.opponentTeam || null,
      statType: leg.statType || '',
      line: leg.line ?? null,
      overUnder: (leg.overUnder as 'Over' | 'Under' | null) || null,
      odds: leg.odds || '',
      gameId: leg.gameId || null,
      gameDate: leg.gameDate || null,
      sportsbook: trackingError?.parlayMetadata?.sportsbook || selectedSportsbook,
      marketKey: leg.marketKey || null,
      source: 'ai' as const,
      provenance: leg.provenance ?? 'generation',
      predictionId: leg.predictionId,
      isB2b: leg.isB2b ?? null,
      blowoutRisk: leg.blowoutRisk ?? null,
      isHomeGame: leg.isHomeGame ?? null,
      leagueKey: leg.leagueKey,
    }));
    addLegsToBuilder(builderLegs);
    setTrackingError(null);
    setBuilderExpanded(true);
  };

  return {
    trackingKeys,
    trackingError,
    isParlayTracked,
    trackParlay,
    handleTrackHealthyLegs,
    handleAddToBuilder,
    setTrackingError,
  };
}
